import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { User, Globe, Award, Crown, Save } from 'lucide-react';

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, isPremium, updateProfile } = useAuth();
  const [level, setLevel] = useState(user?.level || 'beginner');
  const [language, setLanguage] = useState(user?.language || 'it');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ level, language });
      i18n.changeLanguage(language);
      localStorage.setItem('language', language);
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const levelColors = {
    beginner: 'from-green-500 to-emerald-400',
    intermediate: 'from-blue to-cyan-400',
    expert: 'from-purple-500 to-pink-500'
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2">
            {t('nav.profile')}
          </h1>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-8 mb-8"
        >
          {/* User Info */}
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-white/5">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
              <User className="w-10 h-10 text-black" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-white">{user?.username}</h2>
              <p className="text-zinc-400">{user?.email}</p>
              {isPremium && (
                <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber to-orange-500 rounded-full">
                  <Crown className="w-4 h-4 text-black" />
                  <span className="text-sm font-bold text-black">{t('common.premium')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-6">
            {/* Level */}
            <div>
              <label className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                <Award className="w-4 h-4" />
                {t('dashboard.yourLevel')}
              </label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="beginner">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${levelColors.beginner}`} />
                      {t('levels.beginner')}
                    </div>
                  </SelectItem>
                  <SelectItem value="intermediate">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${levelColors.intermediate}`} />
                      {t('levels.intermediate')}
                    </div>
                  </SelectItem>
                  <SelectItem value="expert">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${levelColors.expert}`} />
                      {t('levels.expert')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Language */}
            <div>
              <label className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                <Globe className="w-4 h-4" />
                {t('profile.language')}
              </label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="it">
                    <div className="flex items-center gap-2">
                      🇮🇹 Italiano
                    </div>
                  </SelectItem>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      🇬🇧 English
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
            className="w-full mt-8 bg-gradient-to-r from-amber to-orange-500 text-black font-bold py-6 rounded-full"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {t('common.save')}
              </>
            )}
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-6"
        >
          <h3 className="font-heading text-lg font-bold text-white mb-4">{t('profile.accountStatus')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0A0A0A] rounded-lg p-4">
              <p className="text-zinc-500 text-sm">{t('profile.level')}</p>
              <p className={`font-bold text-lg bg-gradient-to-r ${levelColors[level]} bg-clip-text text-transparent`}>
                {t(`levels.${level}`)}
              </p>
            </div>
            <div className="bg-[#0A0A0A] rounded-lg p-4">
              <p className="text-zinc-500 text-sm">{t('dashboard.status')}</p>
              <p className={`font-bold text-lg ${isPremium ? 'text-amber' : 'text-zinc-400'}`}>
                {isPremium ? t('common.premium') : t('common.free')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
